import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card } from '../../components/ui/Card';
import { SeverityIndicator } from '../../components/ui/SeverityIndicator';
// Mock data for cleaning intervention
const mockCleaningDetail = {
  id: 'c1',
  date: '28 Gennaio 2025',
  time: '09:00 - 11:30',
  supplier: 'CoopService',
  operator: 'Mario R.',
  duration: '2h 30min',
  rooms: [{
    name: 'Soggiorno',
    beforePhoto: 'https://a0.muscache.com/im/pictures/prohost-api/Hosting-1353673811838304349/original/142376af-a8ad-457c-a3af-4b264a5e291c.jpeg?aki_policy=xx_large',
    afterPhoto: 'https://a0.muscache.com/im/pictures/prohost-api/Hosting-1353673811838304349/original/142376af-a8ad-457c-a3af-4b264a5e291c.jpeg?aki_policy=xx_large',
    score: 'excellent'
  }, {
    name: 'Cucina',
    beforePhoto: 'https://a0.muscache.com/im/pictures/hosting/Hosting-1589863564105425260/original/22e98b9a-4848-48d2-8bc9-f75adab5a84d.jpeg?im_w=1200',
    afterPhoto: 'https://a0.muscache.com/im/pictures/hosting/Hosting-1589863564105425260/original/22e98b9a-4848-48d2-8bc9-f75adab5a84d.jpeg?im_w=1200',
    score: 'excellent'
  }, {
    name: 'Bagno',
    beforePhoto: 'https://a0.muscache.com/im/pictures/hosting/Hosting-1589863564105425260/original/8da17c5b-c2d1-4b3e-aa33-a0df921816fa.jpeg?aki_policy=xx_large',
    afterPhoto: 'https://a0.muscache.com/im/pictures/hosting/Hosting-1589863564105425260/original/8da17c5b-c2d1-4b3e-aa33-a0df921816fa.jpeg?aki_policy=xx_large',
    score: 'good'
  }],
  preferences: [{
    text: 'Lenzuola stirate',
    completed: true
  }, {
    text: 'Cuscini divano sistemati',
    completed: true
  }, {
    text: 'Luci ingresso accese',
    completed: true
  }, {
    text: 'Prodotti bio utilizzati',
    completed: true
  }]
};
// Mock data for maintenance intervention
const mockMaintenanceDetail = {
  id: 'm1',
  date: '25 Gennaio 2025',
  problemType: 'Oggetto Rotto',
  problemEmoji: '🔧',
  description: 'Rubinetto bagno che perde acqua',
  severity: 'Grave',
  resolvedBy: 'IdraulicoPro Srl',
  interventionTime: '45min',
  reportedBy: 'Mario R. - Fornitore Pulizie',
  reportDate: '25 Gen, 10:32',
  originalDescription: 'Il rubinetto del bagno principale perde acqua costantemente. La perdita è abbastanza consistente e sta bagnando il mobile sottostante.',
  reportPhotos: ['https://a0.muscache.com/im/pictures/hosting/Hosting-1589863564105425260/original/8da17c5b-c2d1-4b3e-aa33-a0df921816fa.jpeg?aki_policy=xx_large'],
  solutionType: 'Sostituzione',
  solutionDescription: 'Sostituito il rubinetto con uno nuovo dello stesso modello. Verificata tenuta e assenza di perdite.',
  finalPhoto: 'https://a0.muscache.com/im/pictures/hosting/Hosting-1589863564105425260/original/8da17c5b-c2d1-4b3e-aa33-a0df921816fa.jpeg?aki_policy=xx_large',
  technicianRatings: {
    descriptionAccuracy: 4,
    photoAccuracy: 5,
    actualSeverity: 3,
    severityNote: 'Meno grave del previsto'
  }
};
export function InterventionDetailPage() {
  const {
    t
  } = useLanguage();
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || 'cleaning';
  const isCleaning = type === 'cleaning';
  const data = isCleaning ? mockCleaningDetail : mockMaintenanceDetail;
  const getScoreColor = (score: string) => {
    switch (score) {
      case 'excellent':
        return 'bg-green-100 text-green-700';
      case 'good':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-orange-100 text-orange-700';
    }
  };
  const getScoreLabel = (score: string) => {
    switch (score) {
      case 'excellent':
        return t.host.interventionDetail.score.excellent;
      case 'good':
        return t.host.interventionDetail.score.good;
      default:
        return t.host.interventionDetail.score.needsImprovement;
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-1030">
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 pb-12" data-id="element-1031">
        {/* Header */}
        <motion.div className="flex items-center gap-4 mb-8" initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-1032">
          <button onClick={() => navigate('/host/interventions')} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-1033">
            <ArrowLeft className="w-6 h-6 text-gray-600" data-id="element-1034" />
          </button>
          <h1 className="text-2xl font-bold text-navy" data-id="element-1035">
            {isCleaning ? `${t.host.interventionDetail.title} ${mockCleaningDetail.date.split(' ')[0]} ${mockCleaningDetail.date.split(' ')[1]}` : `${t.host.interventionDetail.maintenanceTitle} ${mockMaintenanceDetail.date.split(' ')[0]} ${mockMaintenanceDetail.date.split(' ')[1]}`}
          </h1>
        </motion.div>

        {isCleaning ?
      // Cleaning Intervention Detail
      <div className="space-y-6" data-id="element-1036">
            {/* Summary Card */}
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-1037">
              <Card className="p-6" data-id="element-1038">
                <h3 className="text-lg font-bold text-navy mb-4" data-id="element-1039">
                  {t.host.interventionDetail.summary}
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-1040">
                  <div data-id="element-1041">
                    <span className="text-gray-500" data-id="element-1042">
                      {t.host.interventionDetail.date}
                    </span>
                    <p className="font-medium text-gray-900" data-id="element-1043">
                      {mockCleaningDetail.date}
                    </p>
                  </div>
                  <div data-id="element-1044">
                    <span className="text-gray-500" data-id="element-1045">
                      {t.host.interventionDetail.time}
                    </span>
                    <p className="font-medium text-gray-900" data-id="element-1046">
                      {mockCleaningDetail.time}
                    </p>
                  </div>
                  <div data-id="element-1047">
                    <span className="text-gray-500" data-id="element-1048">
                      {t.host.interventionDetail.supplier}
                    </span>
                    <p className="font-medium text-gray-900" data-id="element-1049">
                      {mockCleaningDetail.supplier}
                    </p>
                  </div>
                  <div data-id="element-1050">
                    <span className="text-gray-500" data-id="element-1051">
                      {t.host.interventionDetail.operator}
                    </span>
                    <p className="font-medium text-gray-900" data-id="element-1052">
                      {mockCleaningDetail.operator}
                    </p>
                  </div>
                  <div className="col-span-2" data-id="element-1053">
                    <span className="text-gray-500" data-id="element-1054">
                      {t.host.interventionDetail.totalDuration}
                    </span>
                    <p className="font-medium text-gray-900" data-id="element-1055">
                      {mockCleaningDetail.duration}
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Photo Comparison */}
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} data-id="element-1056">
              <Card className="p-6" data-id="element-1057">
                <h3 className="text-lg font-bold text-navy mb-4" data-id="element-1058">
                  {t.host.interventionDetail.photoComparison}
                </h3>
                <div className="space-y-6" data-id="element-1059">
                  {mockCleaningDetail.rooms.map(room => <div key={room.name} data-id="element-1060">
                      <div className="flex items-center justify-between mb-3" data-id="element-1061">
                        <span className="font-medium text-gray-900" data-id="element-1062">
                          {room.name}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(room.score)}`} data-id="element-1063">
                          {getScoreLabel(room.score)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3" data-id="element-1064">
                        <div data-id="element-1065">
                          <span className="text-xs text-gray-500 mb-1 block" data-id="element-1066">
                            {t.host.interventionDetail.before}
                          </span>
                          <img src={room.beforePhoto} alt={`${room.name} before`} className="w-full aspect-[4/3] object-cover rounded-lg" data-id="element-1067" />
                        </div>
                        <div data-id="element-1068">
                          <span className="text-xs text-gray-500 mb-1 block" data-id="element-1069">
                            {t.host.interventionDetail.after}
                          </span>
                          <img src={room.afterPhoto} alt={`${room.name} after`} className="w-full aspect-[4/3] object-cover rounded-lg" data-id="element-1070" />
                        </div>
                      </div>
                    </div>)}
                </div>
              </Card>
            </motion.div>

            {/* Preferences Followed */}
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} data-id="element-1071">
              <Card className="p-6" data-id="element-1072">
                <h3 className="text-lg font-bold text-navy mb-4" data-id="element-1073">
                  {t.host.interventionDetail.preferencesFollowed}
                </h3>
                <div className="space-y-3" data-id="element-1074">
                  {mockCleaningDetail.preferences.map((pref, index) => <div key={index} className="flex items-center gap-3" data-id="element-1075">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${pref.completed ? 'bg-green-100' : 'bg-red-100'}`} data-id="element-1076">
                        {pref.completed ? <Check className="w-4 h-4 text-green-600" data-id="element-1077" /> : <span className="text-red-600 text-sm" data-id="element-1078">✕</span>}
                      </div>
                      <span className="text-gray-700" data-id="element-1079">{pref.text}</span>
                    </div>)}
                </div>
              </Card>
            </motion.div>
          </div> :
      // Maintenance Intervention Detail
      <div className="space-y-6" data-id="element-1080">
            {/* Summary Card */}
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-1081">
              <Card className="p-6" data-id="element-1082">
                <h3 className="text-lg font-bold text-navy mb-4" data-id="element-1083">
                  {t.host.interventionDetail.summary}
                </h3>
                <div className="space-y-3" data-id="element-1084">
                  <div className="flex items-center gap-2" data-id="element-1085">
                    <span className="text-2xl" data-id="element-1086">
                      {mockMaintenanceDetail.problemEmoji}
                    </span>
                    <span className="font-bold text-gray-900" data-id="element-1087">
                      {mockMaintenanceDetail.problemType}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700" data-id="element-1088">
                      {mockMaintenanceDetail.severity}
                    </span>
                  </div>
                  <p className="text-gray-600" data-id="element-1089">
                    {mockMaintenanceDetail.description}
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-sm pt-2" data-id="element-1090">
                    <div data-id="element-1091">
                      <span className="text-gray-500" data-id="element-1092">
                        {t.host.interventionDetail.resolvedBy}
                      </span>
                      <p className="font-medium text-gray-900" data-id="element-1093">
                        {mockMaintenanceDetail.resolvedBy}
                      </p>
                    </div>
                    <div data-id="element-1094">
                      <span className="text-gray-500" data-id="element-1095">
                        {t.host.interventionsHistory.interventionTime}
                      </span>
                      <p className="font-medium text-gray-900" data-id="element-1096">
                        {mockMaintenanceDetail.interventionTime}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Original Report */}
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} data-id="element-1097">
              <Card className="p-6" data-id="element-1098">
                <h3 className="text-lg font-bold text-navy mb-4" data-id="element-1099">
                  {t.host.interventionDetail.originalReport}
                </h3>
                <div className="space-y-3 text-sm" data-id="element-1100">
                  <div data-id="element-1101">
                    <span className="text-gray-500" data-id="element-1102">
                      {t.host.interventionDetail.reportedBy}
                    </span>
                    <p className="font-medium text-gray-900" data-id="element-1103">
                      {mockMaintenanceDetail.reportedBy}
                    </p>
                  </div>
                  <div data-id="element-1104">
                    <span className="text-gray-500" data-id="element-1105">
                      {t.host.interventionDetail.reportDate}
                    </span>
                    <p className="font-medium text-gray-900" data-id="element-1106">
                      {mockMaintenanceDetail.reportDate}
                    </p>
                  </div>
                  <div data-id="element-1107">
                    <span className="text-gray-500" data-id="element-1108">Descrizione</span>
                    <p className="text-gray-700 mt-1" data-id="element-1109">
                      {mockMaintenanceDetail.originalDescription}
                    </p>
                  </div>
                  <div data-id="element-1110">
                    <span className="text-gray-500 block mb-2" data-id="element-1111">Foto</span>
                    <div className="flex gap-2" data-id="element-1112">
                      {mockMaintenanceDetail.reportPhotos.map((photo, index) => <img key={index} src={photo} alt={`Report photo ${index + 1}`} className="w-24 h-24 object-cover rounded-lg" data-id="element-1113" />)}
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Resolution */}
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} data-id="element-1114">
              <Card className="p-6" data-id="element-1115">
                <h3 className="text-lg font-bold text-navy mb-4" data-id="element-1116">
                  {t.host.interventionDetail.resolution}
                </h3>
                <div className="space-y-3 text-sm" data-id="element-1117">
                  <div data-id="element-1118">
                    <span className="text-gray-500" data-id="element-1119">
                      {t.host.interventionDetail.solutionType}
                    </span>
                    <p className="font-medium text-gray-900" data-id="element-1120">
                      {mockMaintenanceDetail.solutionType}
                    </p>
                  </div>
                  <div data-id="element-1121">
                    <span className="text-gray-500" data-id="element-1122">
                      Descrizione intervento
                    </span>
                    <p className="text-gray-700 mt-1" data-id="element-1123">
                      {mockMaintenanceDetail.solutionDescription}
                    </p>
                  </div>
                  <div data-id="element-1124">
                    <span className="text-gray-500 block mb-2" data-id="element-1125">
                      Foto finale
                    </span>
                    <img src={mockMaintenanceDetail.finalPhoto} alt="Final result" className="w-full max-w-xs aspect-[4/3] object-cover rounded-lg" data-id="element-1126" />
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Technician Rating */}
            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} data-id="element-1127">
              <Card className="p-6" data-id="element-1128">
                <h3 className="text-lg font-bold text-navy mb-4" data-id="element-1129">
                  {t.host.interventionDetail.technicianRating}
                </h3>
                <div className="space-y-6" data-id="element-1130">
                  <div data-id="element-1131">
                    <SeverityIndicator label={t.host.interventionDetail.descriptionAccuracy} value={mockMaintenanceDetail.technicianRatings.descriptionAccuracy} interactive={false} showLabel={false} size="sm" variant="accuracy" data-id="element-1132" />
                  </div>
                  <div data-id="element-1133">
                    <SeverityIndicator label={t.host.interventionDetail.photoAccuracy} value={mockMaintenanceDetail.technicianRatings.photoAccuracy} interactive={false} showLabel={false} size="sm" variant="accuracy" data-id="element-1134" />
                  </div>
                  <div data-id="element-1135">
                    <SeverityIndicator label={t.host.interventionDetail.actualSeverity} value={mockMaintenanceDetail.technicianRatings.actualSeverity} interactive={false} showLabel={true} size="sm" variant="severity" data-id="element-1136" />
                    <p className="text-sm text-gray-500 mt-2" data-id="element-1137">
                      {mockMaintenanceDetail.technicianRatings.severityNote}
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>}
      </main>
    </div>;
}